angular.module('dentalLinksServices')

// CbctUploadService (DCL-212)
// ---------------------------------------------------------------------------
// Uploads a validated CBCT selection directly to S3 via presigned PUT URLs, so
// the file bytes (100 MB - 1.5 GB) never proxy through Rails (60 MB cap).
//
// Flow, driven by CbctStripService:
//   1. start(referralId, selection)  -> POST /referrals/:id/cbct
//        creates the CBCT attachment record (pending) + temp S3 prefix.
//   2. for each batch of files: POST /referrals/:id/cbct/:cbctId/presign
//        returns a presigned PUT url per key; PUT bytes straight to S3 with
//        bounded concurrency (~4), retrying a failed file once.
//   3. finalize(...) -> POST .../finalize  (record -> uploaded)
//      or abort()    -> POST .../abort     (record dropped, XHRs aborted)
//
// Upload shapes (from CbctValidationService.validate()):
//   - 'zip'       : a single .zip File -> one object at "<sourceName>"
//   - 'directory' : many File objects  -> per-file objects preserving relative
//                   paths (webkitRelativePath), sanitized server-side too
//   - 'single'    : one .dcm/other File -> one object at "<name>"
//
// The service is stateless per call: start() returns a "session" object with
// run(onProgress) and cancel(). CbctStripService owns the state machine.
    .factory('CbctUploadService', ['$q', '$http', 'API_ENDPOINT',
        function ($q, $http, API_ENDPOINT) {

            var PRESIGN_BATCH_SIZE = 100;   // keys requested per presign call
            var UPLOAD_CONCURRENCY = 4;     // parallel S3 PUTs
            var MAX_ATTEMPTS = 2;           // initial try + one retry

            // Build the list of {file, path} to upload from a validation summary.
            // 'path' is the S3-relative key (temp prefix is prepended server-side).
            function buildFileEntries(selection) {
                if (selection.kind === 'zip') {
                    return [{file: selection.zipFile, path: selection.sourceName || selection.zipFile.name}];
                }
                if (selection.kind === 'single') {
                    var only = selection.files[0];
                    return [{file: only, path: only.name}];
                }
                // directory: preserve the relative path minus the top folder segment
                // so keys are "<...>/slice" rather than "<folder>/<...>/slice".
                return selection.files.map(function (f) {
                    var rel = f.webkitRelativePath || f.name;
                    var slash = rel.indexOf('/');
                    var path = slash !== -1 ? rel.substring(slash + 1) : rel;
                    return {file: f, path: path};
                });
            }

            function extraHeaders(headersFn) {
                return (typeof headersFn === 'function' && headersFn()) || {};
            }

            // POST helper that carries the optional draft-edit headers.
            function post(url, body, headersFn) {
                return $http.post(url, body || {}, {headers: extraHeaders(headersFn)})
                    .then(function (resp) { return resp.data; });
            }

            // Request presigned URLs for a batch of {file, path} entries.
            function presignBatch(referralId, cbctId, entries, headersFn) {
                var url = API_ENDPOINT + '/referrals/' + referralId + '/cbct/' + cbctId + '/presign';
                var keys = entries.map(function (e) { return {path: e.path, size: e.file.size}; });
                return post(url, {keys: keys}, headersFn).then(function (data) {
                    // Map path -> presigned url for lookup.
                    var byPath = {};
                    (data.uploads || []).forEach(function (u) { byPath[u.path] = u.url; });
                    return byPath;
                });
            }

            // PUT one file to S3. Resolves on 2xx; rejects otherwise. Reports
            // per-file byte progress via onBytes(deltaLoaded). Registers the XHR
            // with the session so cancel() can abort it.
            function putFile(session, url, file, onBytes) {
                return $q(function (resolve, reject) {
                    if (session.cancelled) { reject(new Error('cancelled')); return; }
                    var xhr = new XMLHttpRequest();
                    session.activeXhrs.push(xhr);
                    var lastLoaded = 0;
                    xhr.upload.onprogress = function (e) {
                        if (e.lengthComputable) {
                            onBytes(e.loaded - lastLoaded);
                            lastLoaded = e.loaded;
                        }
                    };
                    xhr.onload = function () {
                        removeXhr(session, xhr);
                        if (xhr.status >= 200 && xhr.status < 300) {
                            onBytes(file.size - lastLoaded); // settle to exact size
                            resolve();
                        } else {
                            reject(new Error('S3 PUT failed with status ' + xhr.status));
                        }
                    };
                    xhr.onerror = function () { removeXhr(session, xhr); reject(new Error('network error')); };
                    xhr.onabort = function () { removeXhr(session, xhr); reject(new Error('cancelled')); };
                    xhr.open('PUT', url, true);
                    xhr.send(file);
                });
            }

            function removeXhr(session, xhr) {
                var i = session.activeXhrs.indexOf(xhr);
                if (i !== -1) { session.activeXhrs.splice(i, 1); }
            }

            // Upload a single entry with one retry, updating progress.
            function uploadEntry(session, entry, presignedUrl, progress) {
                var attempt = 0;
                function tryOnce() {
                    attempt++;
                    var beforeLoaded = progress.loaded;
                    return putFile(session, presignedUrl, entry.file, function (delta) {
                        progress.loaded += delta;
                        progress.report();
                    }).catch(function (err) {
                        if (session.cancelled || attempt >= MAX_ATTEMPTS) { throw err; }
                        // Roll back this file's partial progress before retrying.
                        progress.loaded = beforeLoaded;
                        progress.report();
                        return tryOnce();
                    });
                }
                return tryOnce();
            }

            // Run a batch: presign, then PUT its files with bounded concurrency.
            function runBatch(session, referralId, cbctId, entries, headersFn, progress) {
                return presignBatch(referralId, cbctId, entries, headersFn).then(function (urlByPath) {
                    var queue = entries.slice();
                    function worker() {
                        if (session.cancelled) { return $q.reject(new Error('cancelled')); }
                        var entry = queue.shift();
                        if (!entry) { return $q.resolve(); }
                        var url = urlByPath[entry.path];
                        if (!url) { return $q.reject(new Error('missing presigned url for ' + entry.path)); }
                        return uploadEntry(session, entry, url, progress).then(worker);
                    }
                    var workers = [];
                    var n = Math.min(UPLOAD_CONCURRENCY, queue.length);
                    for (var i = 0; i < n; i++) { workers.push(worker()); }
                    return $q.all(workers);
                });
            }

            // Public: start an upload. Returns a session with run()/cancel().
            //   config: { referralId, selection, headersFn? }
            function start(config) {
                var referralId = config.referralId;
                var selection = config.selection;
                var headersFn = config.headersFn;
                var entries = buildFileEntries(selection);
                var totalBytes = entries.reduce(function (sum, e) { return sum + (e.file.size || 0); }, 0);

                var session = {
                    cbctId: null,
                    cancelled: false,
                    activeXhrs: []
                };

                // run(onProgress): drives start -> presign/PUT batches -> finalize.
                // onProgress(percent 0..100) is called as bytes complete.
                session.run = function (onProgress) {
                    var progress = {
                        loaded: 0,
                        report: function () {
                            var pct = totalBytes > 0 ? Math.min(100, Math.round(this.loaded / totalBytes * 100)) : 0;
                            if (onProgress) { onProgress(pct); }
                        }
                    };

                    var startUrl = API_ENDPOINT + '/referrals/' + referralId + '/cbct';
                    return post(startUrl, {
                        selection_kind: selection.kind,
                        source_name: selection.sourceName,
                        file_count: selection.fileCount,
                        total_bytes: selection.totalSize
                    }, headersFn).then(function (data) {
                        session.cbctId = data.id;

                        // Split into presign batches and upload sequentially by batch
                        // (each batch itself uploads with bounded concurrency).
                        var batches = [];
                        for (var i = 0; i < entries.length; i += PRESIGN_BATCH_SIZE) {
                            batches.push(entries.slice(i, i + PRESIGN_BATCH_SIZE));
                        }
                        var chain = $q.resolve();
                        batches.forEach(function (batch) {
                            chain = chain.then(function () {
                                return runBatch(session, referralId, session.cbctId, batch, headersFn, progress);
                            });
                        });
                        return chain;
                    }).then(function () {
                        if (session.cancelled) { return $q.reject(new Error('cancelled')); }
                        var finalizeUrl = API_ENDPOINT + '/referrals/' + referralId + '/cbct/' + session.cbctId + '/finalize';
                        return post(finalizeUrl, {
                            file_count: selection.fileCount,
                            total_bytes: selection.totalSize
                        }, headersFn);
                    });
                };

                // cancel(): abort in-flight PUTs and drop the server-side record.
                // Returns a promise that resolves once the abort request settles.
                session.cancel = function () {
                    session.cancelled = true;
                    session.activeXhrs.slice().forEach(function (xhr) {
                        try { xhr.abort(); } catch (e) { /* ignore */ }
                    });
                    session.activeXhrs.length = 0;
                    if (!session.cbctId) { return $q.resolve(); }
                    var abortUrl = API_ENDPOINT + '/referrals/' + referralId + '/cbct/' + session.cbctId + '/abort';
                    return post(abortUrl, {}, headersFn).catch(function () { /* best-effort */ });
                };

                return session;
            }

            return {
                start: start,
                // exposed for testing
                buildFileEntries: buildFileEntries
            };
        }]);
